/**
 * Shared primitives for the Marketing Studio domain.
 *
 * Ids are opaque strings here. This package owns the SHAPE of the marketing
 * domain, not its persistence: the db package will decide whether a campaign
 * id is a uuid or a prefixed slug, and control-api will mint them. Typing them
 * as distinct aliases keeps a call that wants a campaign id from silently
 * accepting an asset id, without pretending this package knows the format.
 *
 * Colours are validated hex, the same posture as `BrandingSchema` in
 * `@hipnumber/db` (ADR-55): a value that reaches a stylesheet or an SVG fill
 * must already be six safe hex digits, so the surrounding markup cannot be
 * escaped by a stored string.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Ids
// ---------------------------------------------------------------------------

/** A non-empty id string. The db decides the concrete format. */
export const IdSchema = z.string().min(1);

export type TenantId = string;
export type AuctionId = string;
export type LotId = string;
export type CampaignId = string;
export type CampaignAssetId = string;
export type CampaignAssetVersionId = string;
export type CreativeSpecId = string;
export type BrandKitId = string;
export type BrandKitSnapshotId = string;
export type AuctionTruthSnapshotId = string;
export type TemplateId = string;
export type MediaAssetId = string;
export type ProviderRunId = string;
export type PublishConnectionId = string;
export type UserId = string;

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

/** `#RRGGBB`, lower- or upper-case. The one colour shape the domain accepts. */
export const HexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'expected a #RRGGBB hex colour');
export type HexColor = z.infer<typeof HexColorSchema>;

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/**
 * A generation cost. Stored in the provider's own units for audit, plus a
 * normalised HipNumber cost so routing and cost guards never compare dollars
 * across providers by hand. Never a tenant billing unit (ADR-56: tenants are
 * billed in normalised HipNumber credits, decided elsewhere).
 */
export const MoneySchema = z
  .object({
    /** Integer minor units (e.g. cents) to keep arithmetic exact. */
    amountMinor: z.number().int(),
    /** ISO 4217, upper-case. */
    currency: z.string().length(3),
  })
  .strict();
export type Money = z.infer<typeof MoneySchema>;

// ---------------------------------------------------------------------------
// Pixels
// ---------------------------------------------------------------------------

/** A pixel size. Both dimensions positive integers. */
export const DimensionsSchema = z
  .object({ width: z.number().int().positive(), height: z.number().int().positive() })
  .strict();
export type Dimensions = z.infer<typeof DimensionsSchema>;

/** An ISO-8601 instant. Everything time-shaped in this domain is UTC on the
 *  wire and rendered in the auction's timezone by the surface (ADR-56, the
 *  schedule rule). */
export const InstantSchema = z.string().datetime({ offset: true });
export type Instant = z.infer<typeof InstantSchema>;
